import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Table,
    Badge,
    Input,
    Spinner,
    Pagination,
    PaginationItem,
    PaginationLink,
    Label,
    FormGroup
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
    FaMoneyBillWave,
    FaPlus,
    FaEdit,
    FaSearch,
    FaCheckCircle,
    FaClock,
    FaTimes,
    FaFileInvoice,
    FaTruck,
    FaFilter,
    FaTrashAlt
} from 'react-icons/fa';
import { formatPrice } from '../../helpers/utility';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { reglementsFournisseurActions } from '../../sagas/reglementFournisseurSlice';
import Swal from 'sweetalert2';
import { companyActions } from '../../sagas/companySlice';
import { supplierActions } from '../../sagas/supplierSlice';
import Select from 'react-select';
import './ReglementsFournisseurList.css';
import useSessionState from '../../hooks/useSessionState';

const ReglementsFournisseurList = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { loading, error, list } = useSelector(state => state.reglementsFournisseur);
    const { companies } = useSelector(state => state.companies);
    const { suppliers, loading: suppliersLoading } = useSelector(state => state.suppliers);
    const [currentPage, setCurrentPage] = useSessionState('reglementsFournisseurList.currentPage', 1);
    const [rowsPerPage, setRowsPerPage] = useSessionState('reglementsFournisseurList.rowsPerPage', 10);
    const [sortConfig, setSortConfig] = useSessionState(
        'reglementsFournisseurList.sortConfig',
        { key: 'dateReglement', direction: 'desc' }
    );
    const didRestoreListState = useRef(false);
    const { role } = useSelector(state => state.auth);
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const getMonthDateRange = (monthValue) => {
        if (!monthValue) {
            return { dateFrom: '', dateTo: '' };
        }

        const [year, month] = monthValue.split('-').map(Number);
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);

        return {
            dateFrom: formatDate(firstDay),
            dateTo: formatDate(lastDay),
        };
    };
    const isSuperAdmin = role === 'ROLE_SUPER_ADMIN';

	    const [filters, setFilters] = useSessionState('reglementsFournisseurList.filters', {
	        fournisseurId: null,
	        companyId: null,
	        dateFrom: formatDate(startOfMonth),
        dateTo: formatDate(endOfMonth),
        statut: null,
        query: '',
        month: ''
    });

    useEffect(() => {
        dispatch(reglementsFournisseurActions.listRequest(filters));
    }, [dispatch, filters]);

    useEffect(() => {
        if (!companies || companies.length === 0) {
            dispatch(companyActions.getAll({}));
        }
    }, [companies, dispatch]);

    useEffect(() => {
        if (!suppliers || suppliers.length === 0) {
            dispatch(supplierActions.search({ query: '' }));
        }
    }, [dispatch, suppliers]);


    const handleShowModal = () => {
        navigate("/purchases-module/payments/fournisseur/new");
    };

    const handleEdit = (reglement) => {
        navigate("/purchases-module/payments/fournisseur/edit", { state: { reglement } });
    };

    const handleReload = () => {
        dispatch(reglementsFournisseurActions.listRequest(filters));
    };

    const debouncedSearch = useCallback(
        debounce((query) => {
            dispatch(reglementsFournisseurActions.listRequest({
                ...filters,
                query: query
            }));
        }, 1500), // Wait 1.5 seconds after user stops typing
        [filters.query]
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setFilters(prevFilters => ({
            ...prevFilters,
            query: value
        }));
        debouncedSearch(value);
    };

    const getStatutBadge = (statut) => {
        const badges = {
            'EN_ATTENTE': { color: 'info', icon: FaClock },
            'DECAISSE': { color: 'success', icon: FaCheckCircle },
            'IMPAYE': { color: 'danger', icon: FaTimes },
            'ANNULE': { color: 'danger', icon: FaTimes }
        };

        const badge = badges[statut] || { color: 'secondary', icon: FaClock };
        const Icon = badge.icon;

        return (
            <Badge color={badge.color}>
                <Icon className="me-1" size="12" />
                {t(`reglement.${statut.toLowerCase()}`)}
            </Badge>
        );
    };

    const resolveCompanyInfo = (reglement) => {
        const companyId = reglement?.companyId || reglement?.company?.id || null;
        const matchedCompany = companies?.find((item) => Number(item.id) === Number(companyId));
        const companyName =
            reglement?.company?.name ||
            reglement?.nomCompany ||
            reglement?.companyName ||
            matchedCompany?.name ||
            '';

        return {
            id: companyId,
            name: companyName,
        };
    };

    const renderTypeDetails = (reglement) => {
        if (reglement.typeReglement === 'CAUTION') {
            return (
                <div>
                    <Badge color="info" className="mb-1">
                        {t('reglement.caution')}
                    </Badge>
                </div>
            );
        }

        if (reglement.numFactures) {
            return (
                <div className="d-flex flex-column align-items-center">
                    {reglement.numFactures.split(',').map((invoice, index) => (
                        <Badge key={index} color="primary" className="mb-1" style={{ fontSize: '10px' }}>
                            {invoice.trim()}
                        </Badge>
                    ))}
                </div>
            );
        }

        if (reglement.numBonReceptions) {
            return (
                <div className="d-flex flex-column align-items-center">
                    {reglement.numBonReceptions.split(',').map((receipt, index) => (
                        <Badge key={index} color="success" className="mb-1" style={{ fontSize: '10px' }}>
                            {receipt.trim()}
                        </Badge>
                    ))}
                </div>
            );
        }

        return (
            <div>
                <Badge color="warning" className="mb-1">
                    {t('reglement.avance')}
                </Badge>
            </div>
        );
    };

    const getModeReglementIcon = (mode) => {
        const icons = {
            'ESPECES': '💵',
            'CHEQUE': '📝',
            'VIREMENT': '🏦',
            'CARTE_BANCAIRE': '💳',
            'TRAITE': '📄',
            'AUTRE': '❓'
        };
        return icons[mode] || '❓';
    };

    const getDocumentRefs = (reglement) => {
        if (reglement.numFactures) {
            return reglement.numFactures.split(',').map((item) => item.trim()).filter(Boolean);
        }
        if (reglement.numBonReceptions) {
            return reglement.numBonReceptions.split(',').map((item) => item.trim()).filter(Boolean);
        }
        return [];
    };

    const getRemainingAmount = (reglement) =>
        Math.max(0, Number(reglement.montantRegle || 0) - Number(reglement.montantAffecte || 0));

	    const fournisseurOptions = suppliers?.map((sup) => ({
	        value: sup.id,
	        label: sup.typeFournisseur === 'PARTICULIER'
	            ? `${sup.nom || ''} ${sup.prenom || ''} (${sup.code || ''})`.trim()
	            : `${sup.entreprise || ''} (${sup.code || ''})`.trim(),
	    })) ?? [];

	    const companyOptions = companies?.map((company) => ({
	        value: company.id,
	        label: company.name,
	    })) ?? [];

    const compactSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '30px',
            fontSize: '0.78rem',
            borderColor: '#dbe3ef',
            boxShadow: 'none',
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 8px',
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '30px',
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        menuList: (base) => ({
            ...base,
            maxHeight: '260px',
        }),
    };

    const clearFilters = () => {
        setFilters({
	            query: '',
	            statut: '',
	            fournisseurId: null,
	            companyId: null,
	            dateFrom: formatDate(startOfMonth),
            dateTo: formatDate(endOfMonth),
            month: ''
        });
    };

    const handleAnnulerReglement = (reglement) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('reglement.confirmAnnulation'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(reglementsFournisseurActions.annulerReglement({
                    id: reglement.id,
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.success'),
                            text: t('reglement.annulationSuccess'),
                        });
                        handleReload(); // Refresh the filteredList
                    }
                }));
            }
        });
    }

    // Calculate statistics
    const totalReglements = !loading ? list.length : 0;
    const totalMontant = !loading ? list.filter(r => r.typeReglement !== 'CAUTION').reduce((sum, r) => sum + (r?.montantRegle || 0), 0) : 0;
    const numberOfEnAttente = !loading ? list.filter(r => r.statut === 'EN_ATTENTE' && r.typeReglement !== 'CAUTION').length : 0;
    const totalMontantEnAttente = !loading ? list.filter(r => r.statut === 'EN_ATTENTE' && r.typeReglement !== 'CAUTION').reduce((sum, r) => sum + (r.montantRegle || 0), 0) : 0;
    const totalDecaisse = !loading ? list.filter(r => r.statut === 'DECAISSE').reduce((sum, r) => sum + (r.montantRegle || 0), 0) : 0;
    const numberOfDecaisse = !loading ? list.filter(r => r.statut === 'DECAISSE').length : 0;
    const totalCaution = !loading ? list.filter(r => r.typeReglement === 'CAUTION').reduce((sum, r) => sum + (r.montantRegle || 0), 0) : 0;
    const reglementsCaution = !loading ? list.filter(r => r.typeReglement === 'CAUTION').length : 0;
    const getSortValue = (reglement, key) => {
        switch (key) {
            case 'dateReglement':
            case 'dateDecaissement':
            case 'dateEcheance':
                return reglement[key] ? new Date(reglement[key]).getTime() || 0 : 0;
            case 'montantRegle':
            case 'montantAffecte':
                return Number(reglement[key] || 0);
            case 'remaining':
                return Math.max(0, Number(reglement.montantRegle || 0) - Number(reglement.montantAffecte || 0));
            default:
                return '';
        }
    };

    const sortedReglements = [...(list || [])].sort((a, b) => {
        const left = getSortValue(a, sortConfig.key);
        const right = getSortValue(b, sortConfig.key);
        return sortConfig.direction === 'asc' ? left - right : right - left;
    });

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortIndicator = (key) =>
        sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';

    const totalPages = Math.max(1, Math.ceil(sortedReglements.length / rowsPerPage));
    const currentPageSafe = Math.min(currentPage, totalPages);
    const paginatedReglements = sortedReglements.slice((currentPageSafe - 1) * rowsPerPage, currentPageSafe * rowsPerPage);
    const pageStart = sortedReglements.length === 0 ? 0 : (currentPageSafe - 1) * rowsPerPage + 1;
    const pageEnd = Math.min(currentPageSafe * rowsPerPage, sortedReglements.length);
    const navigate = useNavigate();

    useEffect(() => {
        if (!didRestoreListState.current) {
            didRestoreListState.current = true;
            return;
        }
        setCurrentPage(1);
	    }, [filters.fournisseurId, filters.companyId, filters.dateFrom, filters.dateTo, filters.statut, filters.query, filters.month, rowsPerPage]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };


    const handleDecaisserReglement = (reglement) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('reglement.confirmDecaissement'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(reglementsFournisseurActions.decaisserReglement({
                    id: reglement.id,
                    dateDecaissement: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.success'),
                            text: t('reglement.decaissementSuccess'),
                        });

                        handleReload(); // Refresh the filteredList
                    }
                }));
            }
        });
    };

    return (
        <Container fluid className="supplier-payments-page payments-compact-page">
            <div className="supplier-payments-header">
                <div>
                    <div className="supplier-payments-eyebrow">Achats</div>
                    <h2>
                        <FaMoneyBillWave className="text-primary me-2" />
                        {t('reglement.listFournisseur')}
                    </h2>
                    <p>Suivi des avances, paiements BR/factures, decaissements et montants restant a lettrer.</p>
                </div>
                <Button color="primary" className="supplier-payments-primary-action" onClick={handleShowModal}>
                    <FaPlus className="me-2" />
                    {t('reglement.new')}
                </Button>
            </div>

            <Row className="g-3 mb-3">
                <Col lg={3} md={6} sm={6} xs={12}>
                    <Card className="supplier-payments-metric-card h-100">
                        <CardBody className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                        {t('reglement.totalReglements')}
                                    </div>
                                    <div className="h3 mb-0 font-weight-bold">{totalReglements}</div>
                                    <div className="text-xs text-primary mt-2">
                                        {formatPrice(totalMontant)}
                                    </div>
                                </div>
                                <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                                    <FaMoneyBillWave className="text-primary h3 mb-0" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} sm={6} xs={12}>
                    <Card className="supplier-payments-metric-card h-100">
                        <CardBody className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                                        {t('reglement.enAttente')}
                                    </div>
                                    <div className="h3 mb-0 font-weight-bold">{numberOfEnAttente}</div>
                                    <div className="text-xs text-warning mt-2">
                                        {formatPrice(totalMontantEnAttente)}
                                    </div>
                                </div>
                                <div className="rounded-circle bg-warning bg-opacity-10 p-3">
                                    <FaClock className="text-warning h3 mb-0" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} sm={6} xs={12}>
                    <Card className="supplier-payments-metric-card h-100">
                        <CardBody className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                                        {t('reglement.totalDecaisse')}
                                    </div>
                                    <div className="h3 mb-0 font-weight-bold">{numberOfDecaisse}</div>
                                    <div className="text-xs text-success mt-2">
                                        {formatPrice(totalDecaisse)}
                                    </div>
                                </div>
                                <div className="rounded-circle bg-success bg-opacity-10 p-3">
                                    <FaCheckCircle className="text-success h3 mb-0" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} sm={6} xs={12}>
                    <Card className="supplier-payments-metric-card h-100">
                        <CardBody className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                                        {t('reglement.caution')}
                                    </div>
                                    <div className="h3 mb-0 font-weight-bold">{reglementsCaution}</div>
                                    <div className="text-xs text-info mt-2">
                                        {formatPrice(totalCaution)}
                                    </div>
                                </div>
                                <div className="rounded-circle bg-info bg-opacity-10 p-3">
                                    <FaMoneyBillWave className="text-info h3 mb-0" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>



            <Card className="supplier-payments-panel mb-1">
                <CardBody>
                    <div className="supplier-payments-panel-title">
                        <FaFilter />
                        <span>{t('common.filters', 'Filtres')}</span>
                    </div>
	                    <Row className="g-2 align-items-end supplier-filter-row">
	                        <Col className="supplier-filter-search">
                            <FormGroup className="mb-0">
                                <Label className="mb-1">{t('reglement.search')}</Label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaSearch />
                                    </span>
                                    <Input
                                        type="text"
                                        placeholder={t('reglement.searchPlaceholder')}
                                        value={filters.query}
                                        onChange={e => {
                                            const value = e.target.value.toUpperCase();
                                            setFilters(prevFilters => ({
                                                ...prevFilters,
                                                query: value
                                            }));
                                            debouncedSearch(value);
                                        }}
                                        className="text-uppercase"
                                        style={{ textTransform: 'uppercase' }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                debouncedSearch(e.target.value.toUpperCase());
                                            }
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </Col>
		                        <Col className="supplier-filter-select supplier-filter-supplier">
	                            <FormGroup className="mb-0">
	                                <Label className="mb-1">{t('reglement.fournisseur')}</Label>
                                <Select
                                    options={fournisseurOptions}
                                    isLoading={suppliersLoading}
                                    isClearable
                                    placeholder={t('reglement.fournisseur')}
                                    value={fournisseurOptions.find(option => option.value === filters.fournisseurId) || null}
	                                    onChange={(selectedOption) => setFilters((prev) => ({
	                                        ...prev,
	                                        fournisseurId: selectedOption?.value ?? null
	                                    }))}
	                                    styles={compactSelectStyles}
	                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
	                                    menuPosition="fixed"
	                                />
	                            </FormGroup>
	                        </Col>
		                        <Col className="supplier-filter-select supplier-filter-company">
	                            <FormGroup className="mb-0">
	                                <Label className="mb-1">{t('company.company', 'Entreprise')}</Label>
	                                <Select
	                                    options={companyOptions}
	                                    isClearable
	                                    placeholder={t('company.company', 'Entreprise')}
	                                    value={companyOptions.find(option => Number(option.value) === Number(filters.companyId)) || null}
		                                    onChange={(selectedOption) => setFilters((prev) => ({
		                                        ...prev,
		                                        companyId: selectedOption?.value ?? null
		                                    }))}
		                                    styles={compactSelectStyles}
		                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
		                                    menuPosition="fixed"
		                                />
	                            </FormGroup>
	                        </Col>
		                        <Col className="supplier-filter-month">
                            <FormGroup className="mb-0">
                                <Label for="month" className="mb-1">{t('common.month', 'Mois')}</Label>
                                <Input
                                    type="month"
                                    id="month"
                                    value={filters.month}
                                    onChange={e => {
                                        const month = e.target.value;
                                        const { dateFrom, dateTo } = getMonthDateRange(month);
                                        setFilters(f => ({
                                            ...f,
                                            month,
                                            dateFrom: dateFrom || f.dateFrom,
                                            dateTo: dateTo || f.dateTo,
                                        }));
                                    }}
                                />
                            </FormGroup>
                        </Col>
	                        <Col className="supplier-filter-date">
                            <FormGroup className="mb-0">
                                <Label for="dateFrom" className="mb-1">Date début</Label>
                                <Input
                                    type="date"
                                    id="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value, month: '' }))}
                                />
                            </FormGroup>
                        </Col>
	                        <Col className="supplier-filter-date">
                            <FormGroup className="mb-0">
                                <Label for="dateTo" className="mb-1">Date fin</Label>
                                <Input
                                    type="date"
                                    id="dateTo"
                                    value={filters.dateTo}
                                    onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value, month: '' }))}
                                />
                            </FormGroup>
                        </Col>
	                        <Col className="supplier-filter-status">
                            <FormGroup className="mb-0">
                                <Label className="mb-1">{t('reglement.statut')}</Label>
                                <Input
                                    type="select"
                                    value={filters.statut}
                                    onChange={(e) => setFilters({ ...filters, statut: e.target.value })}
                                >
                                    <option value="">{t('reglement.allStatuts')}</option>
                                    <option value="EN_ATTENTE">{t('reglement.enAttente')}</option>
                                    <option value="DECAISSE">{t('reglement.decaisse', 'Décaissé')}</option>
                                    <option value="ANNULE">{t('reglement.annule')}</option>
                                </Input>
                            </FormGroup>
                        </Col>
	                        <Col className="d-flex align-items-end supplier-filter-clear-col">
                            <Button
                                color="secondary"
                                onClick={clearFilters}
                                title={t('common.clearFilters')}
                                className="mb-0"
                                style={{ minHeight: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <FaFilter />
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            <Card className="supplier-payments-table-shell">
                <CardBody>
                    <div className="supplier-payments-table-header">
                        <div>
                            <h5>{t('reglement.reglements', 'Reglements')}</h5>
                            <span>
                                {t('common.paginationInfo', {
                                    defaultValue: '{{start}}-{{end}} sur {{total}}',
                                    start: pageStart,
                                    end: pageEnd,
                                    total: list?.length || 0,
                                })}
                            </span>
                        </div>
                        <div className="supplier-payments-table-controls">
                            <span>{t('common.lines', 'Lignes')}</span>
                            <Input
                                type="select"
                                value={rowsPerPage}
                                onChange={e => setRowsPerPage(Number(e.target.value))}
                                title={t('common.lines', 'Lignes')}
                            >
                                {[10, 20, 50, 100].map((size) => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </Input>
                            <strong>{formatPrice(list.reduce((sum, r) => sum + (r?.montantRegle || 0), 0))}</strong>
                        </div>
                    </div>
                    <Row>
                        <Col>

                            {loading ? (
                                <div className="text-center my-4"><Spinner color="primary" /></div>
                            ) : error ? (
                                <div className="text-danger">Erreur lors du chargement des réglements.</div>
                            ) : (<>
                                <Table hover responsive className="supplier-payments-table mb-0">
                                    <thead>
                                        <tr>
	                                            <th>
                                                    <div>{t('reglement.reference')}</div>
                                                    <div
                                                        className="small text-muted mt-1"
                                                        onClick={() => handleSort('dateReglement')}
                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    >
                                                        {t('reglement.dateReglement')}{sortIndicator('dateReglement')}
                                                    </div>
                                                </th>
	                                            <th>{t('reglement.fournisseur')}</th>
	                                            <th>{t('reglement.documents', 'Documents')}</th>
	                                            <th className="text-end">
                                                    <div
                                                        onClick={() => handleSort('montantRegle')}
                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    >
                                                        {t('reglement.montantRegle')}{sortIndicator('montantRegle')}
                                                    </div>
                                                    <div
                                                        className="small text-muted mt-1"
                                                        onClick={() => handleSort('montantAffecte')}
                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    >
                                                        {t('reglement.affecte')}{sortIndicator('montantAffecte')}
                                                    </div>
                                                    <div
                                                        className="small text-muted"
                                                        onClick={() => handleSort('remaining')}
                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    >
                                                        {t('reglement.resteAPayer', 'Reste')}{sortIndicator('remaining')}
                                                    </div>
                                                </th>
	                                            <th>
                                                    <div>{t('reglement.statut')}</div>
                                                    <div
                                                        className="small text-muted mt-1"
                                                        onClick={() => handleSort('dateDecaissement')}
                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    >
                                                        {t('reglement.dateDecaissement', 'Date décaissement')}{sortIndicator('dateDecaissement')}
                                                    </div>
                                                    <div
                                                        className="small text-muted"
                                                        onClick={() => handleSort('dateEcheance')}
                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    >
                                                        {t('reglement.dateEcheance')}{sortIndicator('dateEcheance')}
                                                    </div>
                                                </th>
                                            <th className="text-end">{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedReglements.map((reglement) => {
                                            const companyInfo = resolveCompanyInfo(reglement);
                                            const documents = getDocumentRefs(reglement);
                                            const remaining = getRemainingAmount(reglement);

                                            return (
                                                <tr key={reglement.id}>
                                                    <td>
                                                        <div className="supplier-payments-main-ref">{reglement.reference || reglement.id}</div>
                                                        <div className="supplier-payments-subtext">
                                                            {reglement.dateReglement
                                                                ? new Date(reglement.dateReglement).toLocaleDateString('fr-FR')
                                                                : '-'}
                                                            {reglement.createdBy ? ` - ${reglement.createdBy}` : ''}
                                                        </div>
                                                        {reglement.referencePaiement && (
                                                            <div className="supplier-payments-subtext">
                                                                {reglement.referencePaiement}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="supplier-payments-strong">{reglement.nomFournisseur || '-'}</div>
                                                        <div className="supplier-payments-subtext">{companyInfo.name || '-'}</div>
                                                        <div className="supplier-payments-mode">
                                                            <span>{getModeReglementIcon(reglement.modeReglement)}</span>
                                                            {reglement.modeReglement
                                                                ? t(`reglement.${reglement.modeReglement.toLowerCase()}`)
                                                                : '-'}
                                                        </div>
                                                        {reglement.banque && (
                                                            <div className="supplier-payments-subtext">
                                                                {reglement.numeroCheque || '-'} - {reglement.banque}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="mb-2">{renderTypeDetails(reglement)}</div>
                                                        {documents.length > 0 ? (
                                                            <div className="supplier-payments-docs">
                                                                {documents.slice(0, 3).map((documentRef) => (
                                                                    <Badge key={documentRef} color="light" className="text-dark">
                                                                        {documentRef}
                                                                    </Badge>
                                                                ))}
                                                                {documents.length > 3 && (
                                                                    <Badge color="secondary">+{documents.length - 3}</Badge>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="supplier-payments-subtext">
                                                                {t('reglement.noLinkedDocument', 'Aucun document lie')}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="supplier-payments-amount">{formatPrice(reglement.montantRegle)}</div>
                                                        <div className="supplier-payments-subtext">
                                                            {t('reglement.affecte')}: {formatPrice(reglement.montantAffecte || 0)}
                                                        </div>
                                                        {remaining > 0 && (
                                                            <div className="supplier-payments-warning">Reste: {formatPrice(remaining)}</div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="mb-2">{getStatutBadge(reglement.statut)}</div>
                                                        <div className="supplier-payments-subtext">
                                                            {t('reglement.dateEncaissement')}: {reglement.dateDecaissement
                                                                ? new Date(reglement.dateDecaissement).toLocaleDateString('fr-FR')
                                                                : '-'}
                                                        </div>
                                                        <div className="supplier-payments-subtext">
                                                            {t('reglement.dateEcheance')}: {reglement.dateEcheance
                                                                ? new Date(reglement.dateEcheance).toLocaleDateString('fr-FR')
                                                                : '-'}
                                                        </div>
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="supplier-payments-actions">
                                                            {(reglement.statut === 'EN_ATTENTE' || isSuperAdmin) && (
                                                                <Button
                                                                    color="danger"
                                                                    outline
                                                                    title={t('reglement.annuler')}
                                                                    size="sm"
                                                                    onClick={() => handleAnnulerReglement(reglement)}
                                                                >
                                                                    <FaTrashAlt size={12} />
                                                                </Button>
                                                            )}
                                                            {reglement.typeReglement !== 'CAUTION' && (
                                                                <Button
                                                                    color="success"
                                                                    outline
                                                                    title={t('reglement.decaisser')}
                                                                    onClick={() => handleDecaisserReglement(reglement)}
                                                                    size="sm"
                                                                    disabled={reglement.statut === 'DECAISSE' || reglement.statut === 'ANNULE'}
                                                                >
                                                                    <FaCheckCircle size={12} />
                                                                </Button>
                                                            )
                                                            }
                                                            {/* Bouton Éditer */}
	                                                            <Button
	                                                                color="warning"
                                                                    outline
	                                                                title={t('reglement.edit')}
	                                                                onClick={() => handleEdit(reglement)}
                                                                size="sm"
	                                                            >
	                                                                <FaEdit size={12} />
	                                                            </Button>
		                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {list.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4">
                                                    <div className="text-muted">{t('reglement.noReglements')}</div>
                                                </td>
                                            </tr>)
                                        }
                                    </tbody>
                                </Table>
                                <div className="supplier-payments-pagination">
                                    <Pagination size="sm" className="mb-0">
                                        <PaginationItem disabled={currentPageSafe === 1}>
                                            <PaginationLink first onClick={() => handlePageChange(1)} />
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPageSafe === 1}>
                                            <PaginationLink previous onClick={() => handlePageChange(currentPageSafe - 1)} />
                                        </PaginationItem>
                                        <PaginationItem disabled>
                                            <PaginationLink>{currentPageSafe} / {totalPages}</PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPageSafe === totalPages}>
                                            <PaginationLink next onClick={() => handlePageChange(currentPageSafe + 1)} />
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPageSafe === totalPages}>
                                            <PaginationLink last onClick={() => handlePageChange(totalPages)} />
                                        </PaginationItem>
                                    </Pagination>
                                </div></>
                            )}

                        </Col>
                    </Row>
                </CardBody>
            </Card>
        </Container>
    );
};

export default ReglementsFournisseurList;


